import { allocationSchema, paginated, type NodeSummary } from '@hearth/shared';
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Cable, CircleCheck, CircleDashed, ServerCog, Settings2 } from 'lucide-react';
import { EmptyState, PageHeader, SectionHeader } from '@/components/page-header';
import { Pagination } from '@/components/pagination';
import { QueryState } from '@/components/query-state';
import { StatTile } from '@/components/stat-tile';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { apiRequest } from '@/lib/api';
import { nodesQuery } from '@/lib/admin-queries';
import { cn } from '@/lib/utils';

const FILTERS = ['all', 'assigned', 'free'] as const;
type Filter = (typeof FILTERS)[number];

export const Route = createFileRoute('/_app/admin/allocations')({
  validateSearch: (
    s: Record<string, unknown>,
  ): { node?: number; filter?: Filter; page?: number } => ({
    node: Number(s.node) > 0 ? Number(s.node) : undefined,
    filter:
      FILTERS.includes(s.filter as Filter) && s.filter !== 'all' ? (s.filter as Filter) : undefined,
    page: Number(s.page) > 1 ? Number(s.page) : undefined,
  }),
  component: AllocationsOverview,
});

const allocationsQuery = (nodeId: number, filter: Filter, page: number, perPage: number) => ({
  queryKey: ['admin', 'nodes', nodeId, 'allocations', page, filter, perPage],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    apiRequest(
      `/api/application/nodes/${nodeId}/allocations?page=${page}&perPage=${perPage}&filter=${filter}`,
      { schema: paginated(allocationSchema), signal },
    ),
});

/**
 * Every node's ports in one place: how many are assigned and free per node, and a browsable
 * list showing which server holds each. Changes still happen on the node's Allocations tab.
 */
function AllocationsOverview() {
  const nodes = useQuery(nodesQuery);
  return (
    <>
      <PageHeader
        title="Allocations"
        icon={Cable}
        description="The IP and port pairs each node offers, and which server holds each one."
      />
      <QueryState query={nodes}>
        {(list) =>
          list.length === 0 ? (
            <EmptyState
              icon={ServerCog}
              title="No nodes yet"
              action={
                <Button asChild size="sm">
                  <Link to="/admin/nodes/new">Add a node</Link>
                </Button>
              }
            >
              Allocations belong to a node. Add one, then give it the ports its servers may use.
            </EmptyState>
          ) : (
            <Overview nodes={list} />
          )
        }
      </QueryState>
    </>
  );
}

function Overview({ nodes }: { nodes: NodeSummary[] }) {
  // Assigned count per node: one row per request, the total comes from the page meta.
  const assigned = useQueries({
    queries: nodes.map((n) => ({
      ...allocationsQuery(n.id, 'assigned', 1, 1),
      select: (d: { meta: { total: number } }) => d.meta.total,
    })),
  });
  const total = nodes.reduce((sum, n) => sum + n.allocationCount, 0);
  const assignedKnown = assigned.every((q) => q.data !== undefined);
  const assignedTotal = assigned.reduce((sum, q) => sum + (q.data ?? 0), 0);
  const failed = assigned.some((q) => q.isError);
  const shown = (v: number) => (assignedKnown ? v : failed ? '—' : '…');

  return (
    <div className="grid gap-8">
      <section aria-label="Totals" className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatTile
          icon={Cable}
          label="Allocations"
          value={total}
          detail={`across ${nodes.length} ${nodes.length === 1 ? 'node' : 'nodes'}`}
        />
        <StatTile icon={CircleCheck} label="Assigned" value={shown(assignedTotal)} />
        <StatTile icon={CircleDashed} label="Free" value={shown(total - assignedTotal)} />
      </section>

      <section aria-label="By node">
        <SectionHeader title="By node" />
        <div className="table-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Node</TableHead>
                <TableHead className="w-[40%] min-w-40">In use</TableHead>
                <TableHead className="text-right">Free</TableHead>
                <TableHead className="w-10">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {nodes.map((n, i) => {
                const used = assigned[i]?.data;
                return (
                  <TableRow key={n.id}>
                    <TableCell>
                      <Link
                        to="/admin/allocations"
                        search={{ node: n.id }}
                        className="font-medium hover:underline"
                      >
                        {n.name}
                      </Link>
                      <div className="text-muted-foreground font-mono text-xs">
                        {n.fqdn} · {n.location.short}
                      </div>
                    </TableCell>
                    <TableCell>
                      <PortMeter used={used} total={n.allocationCount} label={`${n.name} in use`} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {used === undefined ? '…' : n.allocationCount - used}
                    </TableCell>
                    <TableCell>
                      <Button asChild variant="ghost" size="icon-sm">
                        <Link
                          to="/admin/nodes/$nodeId"
                          params={{ nodeId: String(n.id) }}
                          search={{ tab: 'allocations' }}
                          aria-label={`Manage ${n.name}’s allocations`}
                        >
                          <Settings2 />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </section>

      <Browser nodes={nodes} />
    </div>
  );
}

function PortMeter({ used, total, label }: { used?: number; total: number; label: string }) {
  const pct = used !== undefined && total > 0 ? Math.round((used / total) * 100) : 0;
  return (
    <div className="grid gap-1">
      <span className="text-muted-foreground text-xs tabular-nums">
        {used === undefined ? '…' : `${used} of ${total}`}
      </span>
      <div
        className="bg-muted h-1.5 overflow-hidden rounded-full"
        role="meter"
        aria-label={label}
        aria-valuenow={used ?? 0}
        aria-valuemin={0}
        aria-valuemax={total}
      >
        <div
          className={cn('h-full rounded-full', pct >= 90 ? 'bg-warning' : 'bg-primary')}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** One node's allocations, filterable and paged, read-only. */
function Browser({ nodes }: { nodes: NodeSummary[] }) {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const node = nodes.find((n) => n.id === search.node) ?? nodes[0]!;
  const filter = search.filter ?? 'all';
  const page = search.page ?? 1;
  const query = useQuery({
    ...allocationsQuery(node.id, filter, page, 50),
    placeholderData: keepPreviousData,
  });
  const set = (next: { node?: number; filter?: Filter; page?: number }) =>
    void navigate({
      search: (prev) => ({ ...prev, page: undefined, ...next }),
      replace: true,
      resetScroll: false,
    });

  return (
    <section aria-label="Browse allocations">
      <SectionHeader
        title="Browse"
        description="Assigned ports link to their server."
        actions={
          <Button asChild variant="outline" size="sm">
            <Link
              to="/admin/nodes/$nodeId"
              params={{ nodeId: String(node.id) }}
              search={{ tab: 'allocations' }}
            >
              Add or remove ports
            </Link>
          </Button>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="w-full sm:w-64">
          <Select value={String(node.id)} onValueChange={(v) => set({ node: Number(v) })}>
            <SelectTrigger aria-label="Node" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {nodes.map((n) => (
                <SelectItem key={n.id} value={String(n.id)}>
                  {n.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Tabs
          value={filter}
          onValueChange={(v) => set({ filter: v === 'all' ? undefined : (v as Filter) })}
        >
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="assigned">Assigned</TabsTrigger>
            <TabsTrigger value="free">Free</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <QueryState query={query}>
        {(data) => (
          <div className="grid gap-4">
            <div className="table-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Address</TableHead>
                    <TableHead>Alias</TableHead>
                    <TableHead>Server</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-muted-foreground py-8 text-center">
                        {filter === 'all'
                          ? `${node.name} has no allocations yet.`
                          : `No ${filter} allocations on ${node.name}.`}
                      </TableCell>
                    </TableRow>
                  )}
                  {data.data.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell className="font-mono">
                        {a.ip}:{a.port}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{a.ipAlias ?? '—'}</TableCell>
                      <TableCell>
                        {a.server ? (
                          <Link
                            to="/admin/servers/$serverId"
                            params={{ serverId: String(a.server.id) }}
                            className="hover:underline"
                          >
                            {a.server.name}
                          </Link>
                        ) : (
                          <Badge variant="outline">Free</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {a.notes ?? ''}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Pagination {...data.meta} onPageChange={(p) => set({ page: p })} />
          </div>
        )}
      </QueryState>
    </section>
  );
}
